import { useState } from 'react';
import { get, set } from 'lodash';
import type {
    FieldConfig,
    ValidationRule,
    EntityConfig,
    ValidationErrorState,
    FieldErrorConfig,
    ValidationConfigMap
} from '@makemydeal/dr-shared-ui-utils';
import { validate } from '@makemydeal/dr-shared-ui-utils';
import { BUYER } from '@makemydeal/dr-shared-types';

export type FieldErrorConfigs = Record<
    string,
    FieldErrorConfig | ValidationRule[] | Record<string, FieldErrorConfig | ValidationRule[]>
>;

export const CONTRACTING = 'CONTRACTING';
export const DEFAULT = 'DEFAULT';

export type ValidationContext = typeof CONTRACTING | typeof DEFAULT;

export type ErrorState = { hasError: boolean; errorMessage: string };
export const DEFAULT_ERROR_STATE: ErrorState = { hasError: false, errorMessage: '' };

export function getContractFieldPath(fieldName: string, context: string): string {
    return `${context}.${fieldName}`;
}

export const flattenErrorMessages = (errors: ValidationErrorState): string[] => {
    return errors.flatMap((reason) => {
        if (typeof reason === 'string') {
            return reason;
        } else {
            return reason.errors;
        }
    });
};

const DOT_SEPARATOR = '.';

export const getFieldConfig = (
    contractConfig: Record<string, FieldConfig> | undefined,
    fieldPath: string
): [string, FieldConfig | undefined] => {
    // Direct field match
    if (contractConfig?.[fieldPath]) {
        return [fieldPath, contractConfig[fieldPath]];
    }

    // No evaluation type, can't check nested configs
    if (!contractConfig?.evaluationType) {
        return ['', undefined];
    }

    const configMap = contractConfig.config as ValidationConfigMap;

    // Field in config map
    if (configMap?.[fieldPath]) {
        return [fieldPath, configMap[fieldPath]];
    }

    // Handle nested fields (e.g., "billingAddress.city")
    if (fieldPath.includes(DOT_SEPARATOR)) {
        const [nestedParent, nestedField] = fieldPath.split(DOT_SEPARATOR);
        const parentConfig = configMap?.[nestedParent];

        if (parentConfig?.config) {
            const nestedConfig = parentConfig.config as Record<string, FieldConfig>;
            return [nestedField, nestedConfig[nestedField]];
        }
    }

    return ['', undefined];
};

export type FormConfigValidationsErrors = Record<string, ErrorState | Record<string, ErrorState>>;

export function useFormConfigValidations<T extends FormConfigValidationsErrors>(
    initialFormErrors: T,
    fieldErrorConfigs: FieldErrorConfigs,
    getContractEntityConfigMap?: (entity: string, preferInlineMessage: boolean) => EntityConfig,
    contextEntity?: string
) {
    const [formErrors, setFormErrors] = useState<T>(initialFormErrors);

    const validateDistinctEmails = (fieldName: string, value: string, contractingFieldContext?: Record<string, unknown>) => {
        const emailConfig = getContractEntityConfigMap!('applicantEmails', true);
        const { buyerEmail, coBuyerEmail } = (contractingFieldContext as { buyerEmail: string; coBuyerEmail: string }) || {};
        const values = [value, contextEntity === BUYER ? coBuyerEmail : buyerEmail];
        const input = { [fieldName]: [...values] };
        return validate(input, { [fieldName]: emailConfig }, {});
    };

    const validateField = (
        fieldPath: string,
        value: any,
        validationContext?: ValidationContext,
        contractingFieldContext?: Record<string, unknown>
    ): ErrorState => {
        const config = get(fieldErrorConfigs, fieldPath) as ValidationRule[] | undefined;
        let errorMessage = '';
        const DOT_SEPARATOR = '.';

        const runDefaultValidation = () => {
            if (config) {
                let fieldName = fieldPath;
                if (fieldPath.includes(DOT_SEPARATOR)) {
                    // to handle billingAddress.[fields] notation
                    fieldName = fieldPath.split(DOT_SEPARATOR).pop() as string;
                }

                const input = { [fieldName]: value };
                const validationResult = validate(input, { [fieldName]: { rules: config } }, {});
                errorMessage = flattenErrorMessages(validationResult)[0] || '';
            }
        };

        switch (validationContext) {
            case CONTRACTING: {
                const contractEntityConfigMap = getContractEntityConfigMap!('', true);
                const contractConfig = contractEntityConfigMap[contextEntity as keyof typeof contractEntityConfigMap] as Record<
                    string,
                    FieldConfig
                >;

                let fieldConfig: FieldConfig | undefined;
                [fieldPath, fieldConfig] = getFieldConfig(contractConfig, fieldPath);

                if (contextEntity && contractConfig && fieldConfig && fieldPath) {
                    const input = { [fieldPath]: value };
                    const contractRuleTypes = new Set(fieldConfig.rules?.map((rule) => rule.type));
                    const defaultRulesNotInContractConfig =
                        config?.filter((defaultRule) => !contractRuleTypes.has(defaultRule.type)) || [];
                    const combinedRules = [...fieldConfig.rules!, ...defaultRulesNotInContractConfig];
                    const validationResult = validate(input, { [fieldPath]: { rules: combinedRules } }, contractingFieldContext);
                    let errors = flattenErrorMessages(validationResult);

                    if (fieldPath === 'email') {
                        const emailValidationResult = validateDistinctEmails(fieldPath, value, contractingFieldContext);
                        errors = flattenErrorMessages(emailValidationResult);
                    }
                    errorMessage = errors[0] || '';
                } else {
                    runDefaultValidation();
                }
                break;
            }
            default:
                runDefaultValidation();
                break;
        }

        const errorState = { hasError: !!errorMessage, errorMessage };
        setFormErrors((prev) => set({ ...prev }, fieldPath, errorState));
        return errorState;
    };

    const validatePhoneField = (isValid: boolean) => {
        setFormErrors((prev) => ({
            ...prev,
            phone: {
                hasError: !isValid,
                errorMessage: isValid ? '' : 'Cell Phone is invalid'
            }
        }));
    };

    // Function to reset form errors
    const resetFormErrors = () => {
        setFormErrors(initialFormErrors);
    };

    const resetFormErrorsByField = (fieldPath: keyof T) => {
        const initialFormError = get(initialFormErrors, fieldPath) as ErrorState | undefined;
        if (initialFormError) {
            setFormErrors((prev) => set({ ...prev }, fieldPath, initialFormError));
        }
    };

    const validateContractFields = (fieldValues: Record<string, any>, contractingFieldContext?: Record<string, unknown>) => {
        const contractConfig = getContractEntityConfigMap!(contextEntity!, true) as Record<string, FieldConfig>;
        const fieldErrorState: Record<string, ErrorState> = {};
        Object.entries(fieldValues).forEach(([fieldName, fieldValue]) => {
            const [, fieldConfig] = getFieldConfig(contractConfig, fieldName);
            if (fieldConfig) {
                const input = { [fieldName]: fieldValue };
                const validationResult = validate(input, { [fieldName]: fieldConfig }, contractingFieldContext);
                const [errorMessage, ..._otherMessages] = flattenErrorMessages(validationResult);
                const errorState = { hasError: !!errorMessage, errorMessage };
                fieldErrorState[fieldName] = errorState;
            }

            if (fieldName === 'email') {
                const emailErrorState = validateDistinctEmails(fieldName, fieldValue, contractingFieldContext);
                const [errorMessage, ..._otherMessages] = flattenErrorMessages(emailErrorState);
                const errorState = { hasError: !!errorMessage, errorMessage };
                fieldErrorState[fieldName] = errorState;
            }
        });

        setFormErrors((prev) => ({ ...prev, ...fieldErrorState }));
    };

    return {
        formErrors,
        validateField,
        validatePhoneField,
        resetFormErrors,
        resetFormErrorsByField,
        validateContractFields
    };
}
